import {
  Component,
  ComponentInterface,
  h,
  Prop,
  Host,
  Event,
  EventEmitter,
} from "@stencil/core";
import { store, Unsubscribe } from "@stencil/redux";
import {
  CallStateEnum,
  CobrowseStateEnum,
  DisplayCaptureStateEnum,
} from "../../models";
import {
  DeviceCaptureAction,
  Log,
  WindowEventService,
  DeviceCaptureStopAction,
  TranslateService,
  DeviceService,
  CommonService,
  ErrorHandler,
} from "../../services";
import {
  dispatchDisplayCaptureStateChange,
  dispatchDisplayCaptureError,
  dispatchDisplayCaptureDirectionChange,
} from "../../store/actions";
import { RootState } from "../../store/states";
import {
  CobrowseOriginEnum,
  DisplayCaptureDirectionEnum,
} from "../../models/enums";
import { CobrowseService } from "../../services/cobrowse.service";
import { ConversationTypeEnum } from "@auvious/integrations";

@Component({
  tag: "app-display-capture",
  styleUrl: "app-display-capture.scss",
  shadow: true,
})
export class AppDeviceCapture implements ComponentInterface {
  @Prop() buttonShape: "round" | "flat" = "round";
  @Prop() size: "xs" | "sm" | "md" | "lg" | "block" = "lg";
  @Prop() tooltipVisible = true;
  @Prop() tooltipPosition: "right" | "left" = "right";

  @Prop({ mutable: true }) $state: DisplayCaptureStateEnum;
  @Prop({ mutable: true }) $direction: DisplayCaptureDirectionEnum;
  @Prop({ mutable: true }) $callState: CallStateEnum;
  @Prop({ mutable: true }) $cobrowseState: CobrowseStateEnum;
  @Prop({ mutable: true }) $cobrowseOrigin: CobrowseOriginEnum;
  @Prop({ mutable: true }) $error: Error;
  @Prop({ mutable: true }) $isRemoteScreenShareOn: boolean;
  @Prop({ mutable: true }) $conversationType: ConversationTypeEnum;

  // @Event({ bubbles: false }) started: EventEmitter<void>;
  @Event({ bubbles: false }) stopped: EventEmitter<void>;

  storeUnsubscribe: Unsubscribe;

  dispatchDeviceCaptureStateChange: typeof dispatchDisplayCaptureStateChange;
  dispatchDeviceCaptureError: typeof dispatchDisplayCaptureError;
  dispatchDisplayCaptureDirectionChange: typeof dispatchDisplayCaptureDirectionChange;

  connectedCallback() {
    if (this.storeUnsubscribe) this.storeUnsubscribe();

    this.storeUnsubscribe = store.mapStateToProps(
      this,
      (rootState: RootState) => {
        const {
          call: { state, isRemoteScreenShareOn },
          cobrowse: { origin },
          conversation: { type },
          displayCapture: { direction },
        } = rootState;
        const cobrowseState = rootState.cobrowse.state;
        const deviceCaptureState = rootState.displayCapture.state;
        const deviceCaptureError = rootState.displayCapture.error;
        return {
          $state: deviceCaptureState,
          $direction: direction,
          $cobrowseState: cobrowseState,
          $cobrowseOrigin: origin,
          $error: deviceCaptureError,
          $callState: state,
          $isRemoteScreenShareOn: isRemoteScreenShareOn,
          $conversationType: type,
        };
      }
    );
    store.mapDispatchToProps(this, {
      dispatchDeviceCaptureStateChange: dispatchDisplayCaptureStateChange,
      dispatchDeviceCaptureError: dispatchDisplayCaptureError,
      dispatchDisplayCaptureDirectionChange:
        dispatchDisplayCaptureDirectionChange,
    });
  }

  disconnectedCallback() {
    this.storeUnsubscribe();
  }

  private deviceCaptureRequest() {
    if (this.isDisabled) {
      return;
    }
    ErrorHandler.addBreadcrumb("Display capture | start");
    try {
      if (this.isCobrowseOn) {
        CobrowseService.captureDisplay(false);
      } else {
        WindowEventService.sendMessageToTarget(new DeviceCaptureAction());
        this.dispatchDeviceCaptureStateChange(
          DisplayCaptureStateEnum.loadingStream
        );
        this.dispatchDisplayCaptureDirectionChange(
          DisplayCaptureDirectionEnum.outgoing
        );
      }
    } catch (ex) {
      ErrorHandler.addBreadcrumb("Display capture | error", { ex });
      Log.error(ex);
    }
  }

  private deviceCaptureStop() {
    if (this.isDisabled) {
      return;
    }
    ErrorHandler.addBreadcrumb("Display capture | stop");
    try {
      this.stopped.emit();

      if (this.$conversationType === ConversationTypeEnum.voiceCall) {
        WindowEventService.sendMessageToTarget(new DeviceCaptureStopAction());
        this.dispatchDisplayCaptureDirectionChange(null);
      } else {
        CobrowseService.stopDisplayCapture();
      }
    } catch (ex) {
      Log.error(ex);
    }
  }

  get isSharing() {
    return (
      this.$state === DisplayCaptureStateEnum.sharing &&
      this.$direction === DisplayCaptureDirectionEnum.outgoing
    );
  }
  get isError() {
    return this.$state === DisplayCaptureStateEnum.error;
  }
  get isLoading() {
    return [
      DisplayCaptureStateEnum.loadingStream,
      DisplayCaptureStateEnum.requestingAccess,
    ].includes(this.$state);
  }
  get isCallHold() {
    return this.$callState === CallStateEnum.onHold;
  }
  get isDisabled() {
    return (
      this.isCallHold ||
      this.isLoading ||
      (this.$conversationType === ConversationTypeEnum.cobrowse &&
        this.$cobrowseState === CobrowseStateEnum.connecting) ||
      this.$isRemoteScreenShareOn ||
      (this.$state === DisplayCaptureStateEnum.sharing &&
        this.$direction === DisplayCaptureDirectionEnum.incoming) ||
      (this.$cobrowseState === CobrowseStateEnum.connected &&
        ![ConversationTypeEnum.cobrowse, ConversationTypeEnum.chat].includes(
          this.$conversationType
        ))
    );
  }
  get isCobrowseOn() {
    return [CobrowseStateEnum.connected, CobrowseStateEnum.connecting].includes(
      this.$cobrowseState
    );
  }
  get isSupported() {
    return DeviceService.supportsDisplayCapture;
  }

  get errorMessage(): string {
    if (!this.$error) {
      return;
    }
    let message;
    switch (this.$error.name) {
      case "Error":
        switch (this.$error.message) {
          case "screen-share-unvailable":
            // not suppported or disabled by config
            message = "Action disabled"; // todo: change message
            break;
          case "on-hold":
            // call is on hold. Should not be able to press button
            message = "Call on hold";
            break;
        }
        break;
      case "NotAllowedError":
        switch (this.$error.message) {
          case "Permission denied by system":
            // disabled by system. check preferences
            message =
              "Please enable screen recording in your System Preferences";
            break;
          case "Permission denied":
          default:
            // user pressed cancel
            break;
        }
        break;
      case "NotFoundError":
        // happens in Firefox / MacOS when the used hasn't given access in System Prerefences
        message = "Please enable screen recording in your System Preferences";
        break;
      case "InvalidStateError":
        // firefox: getDisplayMedia must be called from a user gesture handler.
        message = "Not supported browser";
        break;
    }
    if (!!message) {
      CommonService.timer.setTimeout(
        () => this.dispatchDeviceCaptureError(null),
        3000
      );
    }
    return TranslateService.instant(message);
  }

  render() {
    if (!this.isSupported) return null;
    let shareText, stopText;
    if (this.tooltipVisible) {
      shareText = TranslateService.instant("Share screen");
      stopText = TranslateService.instant("Stop sharing");
    }
    return (
      <Host>
        {!this.isSharing && (
          <av-button
            data-tid="device-capture/request"
            color="accent"
            loading={this.isLoading}
            shape={this.buttonShape}
            size={this.buttonShape === "round" ? this.size : "md"}
            tooltip={shareText}
            tooltipPosition={this.tooltipPosition}
            disabled={this.isDisabled}
            onClick={this.deviceCaptureRequest.bind(this)}
          >
            {this.buttonShape === "flat" && shareText}
            {this.buttonShape === "round" && !this.isLoading && (
              <av-asset image="screen-out"></av-asset>
            )}
          </av-button>
        )}
        {this.isSharing && (
          <av-button
            data-tid="device-capture/stop"
            class="pulse-danger"
            color="danger"
            loading={this.isLoading}
            disabled={this.isDisabled}
            shape={this.buttonShape}
            size={this.buttonShape === "round" ? this.size : "md"}
            tooltip={stopText}
            tooltipPosition={this.tooltipPosition}
            onClick={this.deviceCaptureStop.bind(this)}
          >
            {this.buttonShape === "flat" && stopText}
            {this.buttonShape === "round" && !this.isLoading && (
              <av-asset image="screen-stop"></av-asset>
            )}
          </av-button>
        )}
        {this.$error && this.errorMessage && (
          <div class="error">{this.errorMessage}</div>
        )}
      </Host>
    );
  }
}
